import React from "react";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { Download, Filter as FilterIcon } from "lucide-react";

import { Filter } from "../../../ui/Filter";
import { CAMPO } from "../../../ui/Filter/sizes";
import { Input } from "../../../ui/Form/Input";
import { Select } from "../../../ui/Form/Select";

dayjs.extend(customParseFormat);

const API_FORMAT = "YYYY-MM-DD";
const VIEW_FORMAT = "DD/MM/YYYY";

/** O campo de data mostra DD/MM/AAAA, mas a API espera AAAA-MM-DD. */
function paraApi(valor) {
  const data = dayjs(valor, VIEW_FORMAT, true);
  return data.isValid() ? data.format(API_FORMAT) : "";
}

function paraTela(valor) {
  if (!valor) return "";
  const data = dayjs(valor);
  return data.isValid() ? data.format(VIEW_FORMAT) : "";
}

/**
 * Filtro do relatório de agendamentos.
 *
 * Substitui o bloco de Grid + TextField do Material UI que ficava inline no
 * RelatorioAgenda. Os valores continuam vindo do ReportContext, passados por
 * props — por isso este componente não lê store nem chama API.
 */
export function AgendaReportFilter({
  firstData,
  lastData,
  department,
  employeeId,
  status,
  cpf,
  professionals = [],
  hasVeterinary = false,
  showPaid = true,
  exporting = false,
  onChange,
  onFilter,
  onExport,
}) {
  // `profissionalByDepartment` no RelatorioAgenda e `!!profissional && filter(...)`,
  // ou seja, vira `false` enquanto a lista nao carregou. Valor padrao de parametro
  // so cobre `undefined`, entao sem esta guarda o `.map` abaixo derruba a tela.
  const listaProfissionais = Array.isArray(professionals) ? professionals : [];

  const handleSubmit = (event) => {
    if (event) event.preventDefault();
    onFilter();
  };

  const handleClear = () => {
    onChange.department("Banho e tosa");
    onChange.employeeId("TODOS");
    onChange.status("TODOS");
    onChange.cpf("");
    onFilter();
  };

  return (
    <Filter.Root onSubmit={handleSubmit}>
      <Filter.Container>
        <Input.Root
          pickDate
          label="Início"
          value={paraTela(firstData)}
          onChange={(value) => onChange.firstData(paraApi(value))}
        />

        <Input.Root
          pickDate
          label="Fim"
          value={paraTela(lastData)}
          onChange={(value) => onChange.lastData(paraApi(value))}
        />

        <Select.Root
          label="Departamento"
          style={CAMPO.grande}
          value={department}
          onChange={(value) => onChange.department(value)}
        >
          <Select.Option value="Banho e tosa">Banho e Tosa</Select.Option>
          {hasVeterinary && (
            <Select.Option value="Clínica Veterinária">Clínica Veterinária</Select.Option>
          )}
        </Select.Root>

        <Select.Root
          label="Profissionais"
          style={CAMPO.grande}
          value={employeeId}
          onChange={(value) => onChange.employeeId(value)}
        >
          <Select.Option value="TODOS">Todos</Select.Option>
          {listaProfissionais.map((option) => (
            <Select.Option key={option?.employeeId} value={option?.employeeId}>
              {option?.name}
            </Select.Option>
          ))}
        </Select.Root>

        <Select.Root
          label="Status"
          style={CAMPO.medio}
          value={status}
          onChange={(value) => onChange.status(value)}
        >
          <Select.Option value="TODOS">Todos</Select.Option>
          <Select.Option value="AGENDADO">Agendado</Select.Option>
          <Select.Option value="CANCELADO">Cancelado</Select.Option>
          <Select.Option value="ORÇAMENTO ENVIADO">Orçamento enviado</Select.Option>
          {showPaid && <Select.Option value="PAGO">Pago</Select.Option>}
        </Select.Root>

        <Input.Root
          label="CPF"
          mask="999.999.999-99"
          style={CAMPO.medio}
          value={cpf}
          onChange={(value) => onChange.cpf(value)}
        />
      </Filter.Container>

      <Filter.Actions>
        <Filter.Action>
          <Filter.ActionIcon icon={FilterIcon} />
          Filtrar
        </Filter.Action>

        <Filter.Action
          outline
          type="button"
          name="clear-agenda-report"
          onClick={handleClear}
        >
          Limpar
        </Filter.Action>

        <Filter.Action
          outline
          type="button"
          name="export-agenda-report"
          title="Exportar para Excel"
          isLoading={exporting}
          onClick={onExport}
        >
          <Filter.ActionIcon icon={Download} isLoading={exporting} />
        </Filter.Action>
      </Filter.Actions>
    </Filter.Root>
  );
}
